import { computePeaks, createLevelMeter } from './audio.js';
/**
 * Getting a photo or a recording to the server.
 *
 * One file per request as a raw body: Express has no multipart parser, and
 * hand-rolling one is a lot of security-sensitive code for a feature that
 * only ever sends a single file at a time.
 */

const MAX_EDGE = 1600;
const WEBP_QUALITY = 0.85;

/**
 * Redraw a picked image through a canvas before uploading it.
 *
 * The size win is incidental. The reason this is not optional is EXIF: a
 * photo straight off a phone carries the exact coordinates it was taken at,
 * and this app deliberately snaps everyone to a 5 km grid before their
 * location leaves the browser. Uploading the original would hand over the
 * precise position the whole product is built to blur. Re-encoding drops
 * every EXIF field on the floor, including GPS.
 */
export async function prepareImage(file) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/webp', WEBP_QUALITY);
  });
  // Safari only gained canvas WebP recently; PNG is the safe fallback and the
  // server sniffs the bytes either way.
  if (!blob) {
    const png = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    return { blob: png, width, height };
  }
  return { blob, width, height };
}

/** Upload one blob and get back the descriptor a post will carry. */
export async function uploadMedia(blob, { width, height, durationMs, peaks } = {}) {
  const headers = { 'Content-Type': blob.type || 'application/octet-stream' };
  if (width) headers['X-Media-Width'] = String(Math.round(width));
  if (height) headers['X-Media-Height'] = String(Math.round(height));
  if (durationMs) headers['X-Media-Duration'] = String(Math.round(durationMs));
  // Computed here, once, on the machine that made the recording — so every
  // viewer can draw the waveform without downloading and decoding the clip.
  if (peaks?.length) {
    headers['X-Media-Peaks'] = peaks.map((p) => Math.round(p * 100)).join(',');
  }

  const res = await fetch('/api/media', { method: 'POST', headers, body: blob });
  let data = {};
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
  return data.media;
}

/** Ids only. The URL is derived, never stored — origins change. */
export const mediaUrl = (id) => `/api/media/${id}`;

/**
 * Recorders disagree about containers: Chrome and Firefox produce Opus in
 * WebM or Ogg, Safari produces AAC in MP4, and Safari cannot play WebM at
 * all. Nothing here transcodes, so the best available move is to ask for MP4
 * first — it is the one container the other two can also play.
 */
const RECORDER_TYPES = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
];

export function pickRecorderType() {
  if (typeof MediaRecorder === 'undefined') return null;
  return RECORDER_TYPES.find((type) => MediaRecorder.isTypeSupported?.(type)) ?? null;
}

export const canRecord = () => (
  typeof MediaRecorder !== 'undefined'
  && !!navigator.mediaDevices?.getUserMedia
);

/**
 * A recorder blob usually carries no duration, so `<audio>.duration` reads
 * Infinity until the whole clip is buffered. Timing the recording is the only
 * reliable number available here — treat it as a label, not a fact.
 */
export async function startRecording() {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mimeType = pickRecorderType();
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const meter = createLevelMeter(stream);
  const chunks = [];
  const startedAt = Date.now();

  recorder.ondataavailable = (event) => {
    if (event.data?.size) chunks.push(event.data);
  };
  recorder.start();

  // Releasing the microphone is not optional: leave the tracks live and the
  // browser keeps showing the recording indicator after the composer closes.
  const release = () => {
    meter.stop();
    stream.getTracks().forEach((track) => track.stop());
  };

  return {
    recorder,
    level: () => meter.read(),
    /** Abandon the recording without producing a clip. */
    cancel: () => {
      try { if (recorder.state !== 'inactive') recorder.stop(); } catch { /* already stopped */ }
      release();
    },
    stop: () => new Promise((resolve) => {
      recorder.onstop = async () => {
        release();
        // The browser may have ignored the requested type entirely, so read
        // it back rather than trusting the hint.
        const blob = new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' });
        resolve({ blob, durationMs: Date.now() - startedAt, peaks: await computePeaks(blob) });
      };
      recorder.stop();
    }),
  };
}
